import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TextField, 
  InputAdornment, 
  Button, 
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { Filter, Gift, RefreshCw, X as CloseIcon, Send } from 'lucide-react';
import { environment } from '../config/environment';
import { useI18n } from '../i18n';
import { getApiHeadersForGet } from '../utils/apiUtils';
import { buildContestTokenQrLink } from '../utils/contestCouponQrLink';
import SendDigitalCodeDialog from '../components/SendDigitalCodeDialog';

interface CouponCode {
  id: string;
  code: string;
  uses_remaining: number;
  max_uses: number;
  is_active: boolean;
  coupon_pass_id: string;
  created_at: string;
  claim_order?: number | null;
  code_kind?: string | null;
  recipient_email?: string | null;
  sent_at?: string | null;
}

interface CouponPass {
  id: string;
  name: string;
  description?: string;
  contest_id: string;
  contest_name?: string;
  enabled: boolean;
  created_at: string;
  codes: CouponCode[];
  qr_link?: string | null;
  qr_is_claimed?: boolean;
  qr_tag_code?: string | null;
  contest_token?: string | null;
}

const ContestCouponPassesPage: React.FC = () => {
  const { t } = useI18n();
  const [couponPasses, setCouponPasses] = useState<CouponPass[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCouponPass, setExpandedCouponPass] = useState<string | null>(null);
  const [expandedContest, setExpandedContest] = useState<string | null>(null);
  const [selectedQRCode, setSelectedQRCode] = useState<string | null>(null);
  const [sendTarget, setSendTarget] = useState<{ code: CouponCode; contestName: string } | null>(null);

  // Marca un código como enviado en el estado local (sin recargar toda la lista).
  const markCodeSent = (codeId: string, recipientEmail: string, sentAt: string) => {
    setCouponPasses(prev => prev.map(cp => ({
      ...cp,
      codes: cp.codes.map(c => (c.id === codeId ? { ...c, recipient_email: recipientEmail, sent_at: sentAt } : c))
    })));
  };

  useEffect(() => {
    fetchCouponPasses();
  }, []);

  const fetchCouponPasses = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${environment.apiBaseUrl}/contest_couponpasses`, {
        headers: getApiHeadersForGet()
      });
      
      if (!response.ok) {
        throw new Error('Error fetching coupon passes');
      }
      
      const data = await response.json();
      setCouponPasses(data || []);
    } catch (error) {
      console.error('Error fetching coupon passes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para verificar si el término de búsqueda es una subsecuencia del texto
  const isSubsequence = (searchTerm: string, text: string): boolean => {
    if (!text || !searchTerm) return false;
    searchTerm = searchTerm.toLowerCase().trim();
    text = text.toLowerCase().trim();
    if (searchTerm.length === 0) return true;
    if (searchTerm.length > text.length) return false;
    
    let searchIndex = 0;
    for (let i = 0; i < text.length && searchIndex < searchTerm.length; i++) {
      if (text[i] === searchTerm[searchIndex]) {
        searchIndex++;
      }
    }
    return searchIndex === searchTerm.length;
  };

  // Filtrar CouponPasses según el término de búsqueda
  const filteredCouponPasses = couponPasses.filter((couponPass) => {
    const searchValue = searchTerm.trim();
    if (!searchValue) return true;
    
    // Buscar en el nombre del concurso
    const contestName = (couponPass.contest_name || couponPass.contest_id || '').trim();
    if (isSubsequence(searchValue, contestName)) return true;
    
    // Buscar en los códigos de cupón
    return couponPass.codes.some(code => {
      const codeValue = (code.code || '').trim();
      return isSubsequence(searchValue, codeValue);
    });
  });

  // Agrupar CouponPasses por contest_id después del filtrado
  const groupedByContest = filteredCouponPasses.reduce((acc, couponPass) => {
    const contestId = couponPass.contest_id;
    if (!acc[contestId]) {
      acc[contestId] = {
        contest_id: contestId,
        contest_name: couponPass.contest_name || contestId,
        couponPasses: []
      };
    }
    acc[contestId].couponPasses.push(couponPass);
    return acc;
  }, {} as Record<string, { contest_id: string; contest_name: string; couponPasses: CouponPass[] }>);

  // Obtener todos los códigos y QRs de un concurso
  const getAllCodesAndQRs = (contestGroup: { contest_id: string; contest_name: string; couponPasses: CouponPass[] }) => {
    const allCodes: Array<{ code: CouponCode; qr_link: string | null; couponPassName: string; contest_name: string; qr_is_claimed: boolean }> = [];
    contestGroup.couponPasses.forEach(couponPass => {
      const orderedCodes = [...couponPass.codes].sort((a: any, b: any) => {
        const orderA = a.claim_order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.claim_order ?? Number.MAX_SAFE_INTEGER;
        if (orderA !== orderB) return orderA - orderB;
        return String(a.created_at || '').localeCompare(String(b.created_at || ''));
      });

      orderedCodes.forEach(code => {
        const fallbackLink = couponPass.qr_link
          || (couponPass.qr_tag_code ? `https://internal.petflix.io/pet-found?code=${encodeURIComponent(couponPass.qr_tag_code)}` : null);
        const qrLink = buildContestTokenQrLink({
          token: couponPass.contest_token,
          fallbackLink,
        });
        allCodes.push({
          code,
          qr_link: qrLink,
          couponPassName: couponPass.name,
          contest_name: contestGroup.contest_name,
          qr_is_claimed: couponPass.qr_is_claimed || false
        });
      });
    });
    return allCodes;
  };

  if (loading) {
    return (
      <Container className="napc-page" maxWidth="xl" sx={{ py: 4, minHeight: '100vh' }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#6b7280' }}>
            Cargando...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container className="napc-page" maxWidth="xl" sx={{ py: { xs: 3, md: 6 }, minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box>
          <Box className="napc-eyebrow">Reward passes</Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#111827' }}>
            {t('buttonsforms.ListCouponPasses')}
          </Typography>
        </Box>
        <Button
          onClick={fetchCouponPasses}
          variant="outlined"
          startIcon={<RefreshCw className="w-4 h-4" />}
          sx={{ 
            minWidth: 'auto',
            px: 2,
            py: 1
          }}
        >
          {t('buttonsforms.Update')}
        </Button>
      </Box>

      {/* Content */}
      {couponPasses.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Gift className="w-16 h-16 text-gray-300 mb-4" style={{ margin: '0 auto' }} />
          <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
            No hay CouponPasses disponibles
          </Typography>
          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
            Genera códigos de cupón para un concurso para verlos aquí
          </Typography>
        </Box>
      ) : (
        <Box>
          {/* Buscador de CouponPasses */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder={t('buttonsforms.searchPlaceholder') || 'Buscar...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Filter className="w-5 h-5" style={{ color: '#6b7280' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#2563eb',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2563eb',
                  },
                },
              }}
            />
          </Box>
          
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                  <TableCell sx={{ fontWeight: 'bold', color: '#374151', textAlign: 'center' }}>{t('menufilters.Allcontests') || 'Concurso'}</TableCell> 
                  <TableCell sx={{ fontWeight: 'bold', color: '#374151', textAlign: 'center' }}>{t('menufilters.Codes') || 'Códigos'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#374151', textAlign: 'center' }}>{t('menufilters.Status') || 'Estado'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#374151', textAlign: 'center' }}>{t('menufilters.CreatedAt') || 'Creado'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(groupedByContest).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                        {couponPasses.length === 0
                          ? 'No hay concursos con códigos de cupón disponibles'
                          : 'No se encontraron CouponPasses que coincidan con la búsqueda'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  Object.values(groupedByContest).map((contestGroup) => {
                    const allCodesAndQRs = getAllCodesAndQRs(contestGroup);
                    const isExpanded = expandedContest === contestGroup.contest_id;
                    const contestToken = contestGroup.couponPasses.find(cp => cp.contest_token)?.contest_token || null;

                    return (
                      <React.Fragment key={contestGroup.contest_id}>
                        <TableRow hover>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {contestGroup.contest_name}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#6b7280', fontFamily: 'monospace' }}>
                              Token: {contestToken || 'No disponible'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {allCodesAndQRs.length > 0 ? (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  if (isExpanded) {
                                    setExpandedContest(null);
                                  } else {
                                    setExpandedContest(contestGroup.contest_id);
                                  }
                                }}
                                sx={{
                                  textTransform: 'none',
                                  color: '#2563eb',
                                  borderColor: '#2563eb',
                                  '&:hover': {
                                    borderColor: '#1d4ed8',
                                    backgroundColor: '#eff6ff'
                                  }
                                }}
                              >
                                {isExpanded ? 'Ocultar lista de códigos' : 'ver lista de codigo'}
                              </Button>
                            ) : (
                              <Typography variant="body2" sx={{ color: '#9ca3af', fontStyle: 'italic' }}>
                                No hay códigos
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Chip 
                              label={t('menufilters.Active') || 'Activo'}
                              color="success"
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              {contestGroup.couponPasses[0]?.created_at 
                                ? new Date(contestGroup.couponPasses[0].created_at).toLocaleDateString()
                                : '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {isExpanded && allCodesAndQRs.length > 0 && (
                          <TableRow>
                            <TableCell colSpan={4} sx={{ py: 2, backgroundColor: '#f9fafb' }}>
                              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Concurso</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Orden</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Código</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Usos</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Tipo</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>QR Code</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Estado QR</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>Envío digital</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {allCodesAndQRs.map((item, index) => (
                                      <TableRow key={`${item.code.id}-${index}`} hover>
                                        <TableCell sx={{ fontSize: '0.75rem', py: 1, textAlign: 'center' }}>
                                          {item.contest_name}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', py: 1, textAlign: 'center' }}>
                                          {item.code.claim_order || index + 1}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', py: 1, textAlign: 'center' }}>
                                          {item.code.code}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', py: 1, textAlign: 'center' }}>
                                          {item.code.uses_remaining || 0} / {item.code.max_uses || 0}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', py: 1, textAlign: 'center' }}>
                                          {item.code.code_kind === 'printed' ? 'Impreso' : item.code.code_kind === 'digital' ? 'Digital' : 'NA'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1, textAlign: 'center' }}>
                                          {item.qr_link ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                              <img 
                                                src={`https://api.qrserver.com/v1/create-qr-code/?size=60x60&data=${encodeURIComponent(item.qr_link)}`}
                                                alt="QR Code"
                                                loading="lazy"
                                                style={{ 
                                                  width: '60px', 
                                                  height: '60px',
                                                  cursor: 'pointer',
                                                  border: '1px solid #e5e7eb',
                                                  borderRadius: '4px'
                                                }}
                                                onClick={() => setSelectedQRCode(item.qr_link!)}
                                                title={item.qr_link}
                                              />
                                            </Box>
                                          ) : (
                                            <Typography variant="caption" sx={{ color: '#9ca3af', fontStyle: 'italic' }}>
                                              No disponible
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell sx={{ py: 1, textAlign: 'center' }}>
                                          <Typography 
                                            variant="caption" 
                                            sx={{ 
                                              fontWeight: 'bold',
                                              color: item.qr_is_claimed ? '#10b981' : '#ef4444',
                                              fontSize: '0.75rem'
                                            }}
                                          >
                                            {item.qr_is_claimed ? 'Claimed' : 'Unclaimed'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1, textAlign: 'center' }}>
                                          {item.code.code_kind === 'digital' ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                              <Button
                                                variant={item.code.sent_at ? 'outlined' : 'contained'}
                                                size="small"
                                                startIcon={<Send size={14} />}
                                                onClick={() => setSendTarget({ code: item.code, contestName: item.contest_name })}
                                                sx={{
                                                  textTransform: 'none',
                                                  fontSize: '0.7rem',
                                                  py: 0.25,
                                                  ...(item.code.sent_at
                                                    ? { color: '#B01F8E', borderColor: '#F7B7D6' }
                                                    : { backgroundColor: '#ED1790', '&:hover': { backgroundColor: '#B01F8E' } })
                                                }}
                                              >
                                                {item.code.sent_at ? 'Reenviar' : 'Enviar'}
                                              </Button>
                                              {item.code.sent_at && (
                                                <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem', wordBreak: 'break-all' }}>
                                                  {item.code.recipient_email}
                                                </Typography>
                                              )}
                                            </Box>
                                          ) : (
                                            <Typography variant="caption" sx={{ color: '#9ca3af', fontStyle: 'italic' }}>
                                              No aplica
                                            </Typography>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Diálogo para enviar ticket digital */}
      <SendDigitalCodeDialog
        open={!!sendTarget}
        onClose={() => setSendTarget(null)}
        endpoint="couponcodes"
        codeId={sendTarget?.code.id || null}
        codeValue={sendTarget?.code.code}
        contestName={sendTarget?.contestName}
        currentRecipient={sendTarget?.code.recipient_email}
        currentSentAt={sendTarget?.code.sent_at}
        onSent={({ codeId, recipient_email, sent_at }) => markCodeSent(codeId, recipient_email, sent_at)}
      />

      {/* Modal para mostrar QR Code ampliado */}
      {selectedQRCode && (
        <Dialog 
          open={!!selectedQRCode} 
          onClose={() => setSelectedQRCode(null)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: '#2563eb', 
            color: 'white', 
            textAlign: 'center', 
            py: 2,
            m: 0,
            minHeight: 'auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
              QR Code
            </Typography>
            <IconButton
              onClick={() => setSelectedQRCode(null)}
              sx={{ 
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: 3,
              width: '100%'
            }}>
              <Box sx={{ 
                p: 3, 
                backgroundColor: '#ffffff', 
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                maxWidth: '500px'
              }}>
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(selectedQRCode)}`}
                  alt="QR Code"
                  style={{ 
                    width: '100%',
                    maxWidth: '400px',
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', maxWidth: '400px', wordBreak: 'break-all', px: 2 }}>
                {selectedQRCode}
              </Typography>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ justifyContent: 'center', p: 3 }}>
            <Button
              onClick={() => setSelectedQRCode(null)}
              variant="contained"
              sx={{ 
                backgroundColor: '#6b7280',
                color: 'white',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#4b5563'
                }
              }}
            >
              {t('buttonsforms.Close') || 'Cerrar'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default ContestCouponPassesPage;

